/**
 * Guide topics for the functions added in round 4 (recurring entries, single leave days, project rules, who works
 * where, HR tools, protection and the maintenance reports). The older topics are in help-content.ts, help-lead.ts and
 * help-topics.ts; which topic is shown in which tab is decided in GUIDE_TOPICS in @pz/core. Keep the three languages in
 * the same structure; `**text**` is printed bold.
 */

type Item = { title: string; text: string };

export interface ExtraText {
  /** employees and externals: recurring entries, dragging tasks onto a day, dialogs, paragraphs, sorting */
  routine: { title: string; lead: string; items: [Item, Item, Item, Item, Item]; note: string };
  /** employees: single leave days, sickness during leave, which days count, company events */
  leavedays: { title: string; lead: string; items: [Item, Item, Item, Item]; note: string };
  /** project leads and office: approval per project, templates, manual tasks, field values, colours */
  projectrules: { title: string; lead: string; items: [Item, Item, Item, Item, Item]; note: string };
  /** project leads and office: own or all projects, who works where, hour types and plan coverage */
  staffing: { title: string; lead: string; items: [Item, Item, Item, Item]; note: string };
  /** office (HR): reset of the extra hours, sickness during leave, single days, holidays, company events */
  hrtools: { title: string; lead: string; items: [Item, Item, Item, Item, Item]; note: string };
  /** office and administration: role defaults, additional confirmation, who reads which guide */
  protection: { title: string; lead: string; items: [Item, Item, Item]; note: string };
  /** administration: reports of the nightly maintenance and the daily check */
  reports: { title: string; lead: string; items: [Item, Item, Item, Item]; note: string };
}

export const EXTRA: Record<'de' | 'en' | 'tr', ExtraText> = {
  de: {
    routine: {
      title: 'Wiederkehrende Einträge und Komfort beim Buchen',
      lead: 'Termine, die jede Woche gleich sind, Aufgaben per Ziehen buchen und Listen so, wie Sie sie brauchen.',
      items: [
        {
          title: 'Jede Woche wiederholen',
          text: 'Im Buchungsdialog **Jede Woche wiederholen** wählen: Wochentage, Stunden je Tag, ab wann und bis wann (Ende optional), z. B. für die wöchentliche Teambesprechung. Die Einträge entstehen als **Entwürfe**, wenn Sie die Woche öffnen, die laufende Woche auch über Nacht. Feiertage, Urlaub und andere Tage ohne Sollzeit bleiben frei. Sie prüfen und reichen ein wie jede Buchung.',
        },
        {
          title: 'Pausieren, ändern, löschen',
          text: 'In **Meine Woche** unter **Wiederkehrend**. Pausiert oder gelöscht entstehen keine neuen Entwürfe; schon angelegte Einträge bleiben in Ihren Zeiten und lassen sich dort ändern oder löschen.',
        },
        {
          title: 'Aufgaben auf einen Tag ziehen',
          text: 'Eine Aufgabe aus **Favoriten** oder **Zuletzt verwendet** auf einen Tag ziehen öffnet die Buchung für diesen Tag. Favoriten ordnen Sie per Ziehen; eine zuletzt verwendete Aufgabe, zwischen die Favoriten gezogen, wird dort Favorit. Der Tageskopf wird grün mit Haken, wenn die Sollzeit erreicht ist, und in der Hinweisfarbe, wenn mehr gebucht ist.',
        },
        {
          title: 'Dialoge und Absätze',
          text: 'Dialoge lassen sich an der Titelleiste verschieben; die Stelle merkt sich der Browser auf diesem Gerät. Auf breiten Bildschirmen öffnet der Buchungsdialog neben der Woche. Die Eingabetaste beginnt in der Beschreibung einen neuen Absatz; Absätze bleiben in Listen, Freigaben und der Abrechnung erhalten.',
        },
        {
          title: 'Sortieren und Suchen',
          text: 'Ein Klick auf eine Spaltenüberschrift sortiert die Liste, ein zweiter dreht die Reihenfolge um, ein dritter stellt sie zurück; die Wahl gilt für Ihr Konto auf jedem Gerät. **Strg+K** sucht Seiten und Reiter, mit Begriffen aus allen drei Sprachen, dazu Projekte (Nummer oder Name: direkt zu Analyse, Team, Aufgaben …) und, mit dem passenden Recht, Personen; immer nur, was Sie öffnen dürfen.',
        },
      ],
      note: 'Ein wiederkehrender Eintrag bucht nur Aufgaben, die für Sie buchbar sind. Wird die Aufgabe geschlossen, steht beim Eintrag „Aufgabe nicht mehr buchbar“ und es entstehen keine Entwürfe mehr.',
    },
    leavedays: {
      title: 'Einzelne Urlaubstage, Krankheit im Urlaub und Firmentermine',
      lead: 'Was beim Beantragen gilt, welche Tage zählen und wer was sieht.',
      items: [
        {
          title: 'Einzelne Tage',
          text: 'Im Antrag **Einzelne Tage** wählen und die Arbeitstage im Kalender anklicken, auch über mehrere Monate. Wochenenden, Feiertage und freie Tage des Vertrags lassen sich nicht wählen und zählen nie. Tage, zwischen denen nur solche Tage liegen, werden zu einem Eintrag zusammengefasst; der Antrag gilt ganz oder gar nicht.',
        },
        {
          title: 'Krank im Urlaub',
          text: 'Werden Sie im Urlaub krank, melden Sie es mit Attest bzw. Arbeitsunfähigkeitsbescheinigung der **Personalverwaltung**. Sie trägt die Krankheit ein und schreibt die Urlaubstage gut; der Urlaub verlängert sich dadurch nicht. Eine eigene Krankmeldung auf Urlaubstagen ist deshalb nicht möglich.',
        },
        {
          title: 'Welche Tage zählen',
          text: 'Die Vorschau im Antrag zeigt die gezählten Urlaubstage: die Arbeitstage Ihres Vertrags. Für Beschäftigte mit türkischem Feiertagskalender und 5-Tage-Woche kann die Firma festlegen, dass ein Samstag zwischen zwei Urlaubstagen mitzählt. Sonntage und Feiertage zählen nie.',
        },
        {
          title: 'Firmentermine',
          text: 'Firmentermine stehen im **Team**-Kalender und auf der Startseite, ganztägig, halbtags oder mit Uhrzeit. Gilt ein Termin nur für bestimmte Personen, sehen ihn diese in ihrer Zeile und, mit Outlook-Anbindung, als Termin in ihrem Outlook-Kalender. Firmentermine sind Arbeitszeit und mindern die Sollzeit nicht.',
        },
      ],
      note: 'Kolleginnen und Kollegen sehen im Teamkalender „abwesend“; welche Arten sie zusätzlich erkennen (meist Urlaub und Weiterbildung), legt die Firma fest. Krankheit und andere Gesundheitsgründe sehen nur Sie und die Personalverwaltung.',
    },
    projectrules: {
      title: 'Projektregeln: Freigabe, Vorlagen, Aufgaben und Feldwerte',
      lead: 'Was je Projekt festgelegt wird. Die Einstellungen stehen im Projekt unter Übersicht, Aufgaben und Forma.',
      items: [
        {
          title: 'Freigabe der Zeiten',
          text: 'Unter **Übersicht > Freigabe der Zeiten** wählt jedes Projekt: **Büro** (eine Stufe), **Projektleitung** (eine Stufe; ohne andere Projektleitung entscheidet das Büro), **Projektleitung, dann Büro** oder **eigene Freigabe** mit dem Einreichen. Ohne eigene Wahl gilt der Standard der Firma. Eine Änderung gilt für alles, worüber noch nicht entschieden ist; Freigegebenes bleibt freigegeben.',
        },
        {
          title: 'Aufgabenvorlagen',
          text: 'Das Büro pflegt unter **Verwaltung > Aufgabenvorlagen** Listen manueller Aufgaben, z. B. für die TGA-Planung. Im Projekt übernimmt sie, wer dort manuelle Aufgaben anlegen darf, in einem Schritt; Titel, die das Projekt schon hat, werden ausgelassen.',
        },
        {
          title: 'Manuelle Aufgaben: Nummer und Löschen',
          text: 'Nummer und Name lassen sich ändern, die Nummer nur auf eine freie; Buchungen bleiben zugeordnet. Vor dem **Löschen** zeigt der Dialog, was entfällt. Stunden in einem abgeschlossenen Monat oder auf einer Rechnung verhindern das Löschen (dann nur ausblenden); nicht eingereichte Entwürfe bleiben bei der Person. Löschen lässt sich nicht rückgängig machen.',
        },
        {
          title: 'Feldwerte je Gebäude',
          text: 'Im Reiter **Forma** unter **Felder aus Forma** mit **Werte eingrenzen** festlegen, welche Werte eines Feldes das Projekt verwendet, im ganzen Projekt, je Gebäude oder je Standort, z. B. die Geschosse eines Gebäudes. Die Projektanalyse verwendet nur diese Werte; eine Aufgabe mit einem anderen Wert zählt dort als „ohne Angabe“ und wird aufgelistet. Forma bleibt unverändert.',
        },
        {
          title: 'Projektfarbe',
          text: 'Jedes Projekt hat eine Farbe für Woche, Kalender und Auswertungen. Neue Projekte erhalten die am wenigsten genutzte Farbe; ändern beim **Bearbeiten** des Projekts.',
        },
      ],
      note: 'Bei „eigene Freigabe“ sind die Stunden mit dem Einreichen freigegeben; die Person kann das bis zum Monatsabschluss zurückziehen, das Büro kann korrigieren.',
    },
    staffing: {
      title: 'Wer arbeitet wo, eigene Projekte und Stundenarten',
      lead: 'Überblick über die Besetzung der Projekte und darüber, was die Zahlen der Auswertung bedeuten.',
      items: [
        {
          title: 'Meine Projekte oder alle',
          text: 'Die Projektliste zeigt wahlweise **Meine Projekte** (Mitglied oder Projektleitung) oder **Alle Projekte**, die Sie sehen dürfen. Die Wahl gilt für Ihr Konto; Büro und Geschäftsführung starten mit allen, alle anderen mit den eigenen.',
        },
        {
          title: 'Wer arbeitet wo',
          text: 'Auf der Seite **Projekte** unter der Liste, **Wer arbeitet wo**: Projekte und Personen mit den gebuchten Stunden im Zeitraum, je dunkler, desto mehr, dazu Projektleitung und Mitglieder ohne Stunden. Die Projektleitung sieht ihre Projekte; alle Projekte sieht, wer Stunden anderer sehen darf. Entwürfe anderer zählen nicht, das interne Projekt ist nicht enthalten.',
        },
        {
          title: 'Stundenarten',
          text: 'Gebuchte Stunden teilen sich in **abrechenbar**, **inklusive** (Pauschalhonorar), **Projekt, nicht abrechenbar** und **intern**. Persönliche Kennzahlen verwenden nur gebuchte Stunden; abgerechnete Stunden stehen in Projektanalyse und Abrechnung.',
        },
        {
          title: 'Abdeckung der Plan-Ist-Quote',
          text: 'Die Quote bewertet nur erledigte Aufgaben mit Planstunden. Fehlt bei einem Teil der Plan, zeigt die Auswertung „ohne Plan“ mit den Stunden; deckt die Quote weniger als die Hälfte ab, steht ein Hinweis daneben. Dann Planstunden in Forma oder bei den manuellen Aufgaben ergänzen.',
        },
      ],
      note: 'In der Gebäudeansicht der Projektanalyse zeigt Grün den Fortschritt (erledigte Aufgaben) und Blau die Stunden; die Legende steht jeweils daneben.',
    },
    hrtools: {
      title: 'Personal: Saldo zurücksetzen, Krankheit im Urlaub, Feiertage und Termine',
      lead: 'Werkzeuge der Personalverwaltung; jede Änderung wird protokolliert und lässt sich zurücknehmen, soweit angegeben.',
      items: [
        {
          title: 'Saldo zurücksetzen',
          text: 'Unter **Abwesenheiten > Überstunden** setzt **Saldo zurücksetzen** den Saldo einer Person auf 0, z. B. nach der Einführungsphase: Überstunden, Soll/Ist und Hinweise auf fehlende Buchungen zählen ab dem gewählten Tag (nicht in der Zukunft). Buchungen bleiben unverändert, Auszahlungen davor zählen nicht mehr. Die Person wird benachrichtigt; Rückgängig ist möglich.',
        },
        {
          title: 'Krankheit im Urlaub',
          text: 'Eine Krankheit mit Attest auf Urlaubstagen tragen nur Sie ein: Der Dialog zeigt die betroffenen Tage, mit **Urlaub kürzen** gehen die Urlaubstage zurück (§ 9 BUrlG; Türkei: İş Kanunu Art. 56, Yıllık Ücretli İzin Yönetmeliği Art. 6). Der Urlaub verlängert sich nicht.',
        },
        {
          title: 'Einzelne Tage für andere',
          text: 'Auch für andere Personen lassen sich **einzelne Tage** eintragen; zusammenhängende Tage werden ein Eintrag, Wochenenden und Feiertage zählen nie.',
        },
        {
          title: 'Feiertage',
          text: 'Unter **Abwesenheiten > Feiertage** je Kalender: Standard-Feiertage umbenennen, verschieben, auf einen halben Tag setzen, entfernen und wiederherstellen; eigene Tage anlegen, auf Wunsch jedes Jahr (nur feste Daten). Jeder Schritt lässt sich rückgängig machen.',
        },
        {
          title: 'Firmentermine',
          text: 'Unter **Abwesenheiten > Firmentermine**: ganztägig, halbtags oder mit Uhrzeit, mit Notiz und auf Wunsch nur für bestimmte Personen. Diese sehen den Termin in ihrer Zeile, auf der Startseite und, mit Outlook-Anbindung, als Termin „gebucht“ in Outlook (ohne Einladung). Löschen lässt sich rückgängig machen.',
        },
      ],
      note: 'Für Personen mit türkischem Feiertagskalender und 5-Tage-Woche legt **Verwaltung > Einstellungen** fest, ob ein Samstag zwischen zwei Urlaubstagen als Urlaubstag zählt (Standard: aus, günstiger für die Beschäftigten). Maßgeblich ist der Arbeitsvertrag; bitte im Zweifel rechtlich prüfen lassen.',
    },
    protection: {
      title: 'Rollen, Rechte und zusätzliche Bestätigung',
      lead: 'Wer was darf, wie Abrechnung und Kunden zusätzlich geschützt werden und wer welche Anleitung liest.',
      items: [
        {
          title: 'Rollen und einzelne Rechte',
          text: 'Bei jeder Person unter **Rolle und Zugang** zeigt eine Übersicht, welche Rechte jede Rolle mitbringt. Jedes Recht gehört mindestens zu einer Rolle; Mitarbeitende und Externe erhalten bewusst keine Verwaltungsrechte. Einzelne Rechte lassen sich zusätzlich vergeben; bei jedem steht, was es öffnet.',
        },
        {
          title: 'Zusätzliche Bestätigung',
          text: 'Wer das Recht für Abrechnung oder Kunden hat, kann dort einschalten, dass sich das Modul erst nach erneuter Bestätigung öffnet: mit dem Code der Authenticator-App, sonst mit dem Passwort. Die Bestätigung gilt die eingestellten Minuten (5 bis 240). Der Server prüft sie bei jedem Aufruf des Moduls.',
        },
        {
          title: 'Wer welche Anleitung sieht',
          text: 'Externe und Mitarbeitende lesen ihre eigene Anleitung, die Projektleitung zusätzlich die für Mitarbeitende und Externe; Büro, Geschäftsführung und technische Administration sehen alle Reiter.',
        },
      ],
      note: 'Grenzen: Die Bestätigung schützt die Bedienung in Projektzeit, nicht den Zugriff auf Server, Datenbank oder Sicherungen. Wer sich nur mit Microsoft anmeldet, richtet für die Bestätigung im Profil die Authenticator-App ein. Die Texte der Anleitungen sind Teil der Anwendung und nicht geheim.',
    },
    reports: {
      title: 'Berichte der Wartung und tägliche Prüfung',
      lead: 'Was nachts im Hintergrund lief und ob dabei Probleme entstanden sind: unter Verwaltung > Berichte der Wartung.',
      items: [
        {
          title: 'Ein Bericht je Nacht',
          text: 'Jeder Schritt der nächtlichen Wartung mit Ergebnis und Dauer: Forma-Import und Abgleich, wiederkehrende Einträge, Sicherung und Aufbewahrung, Kapazitäten, Dokumente, Export für Auswertungen und das Aufräumen alter Anmeldungen.',
        },
        {
          title: 'Tägliche Prüfung',
          text: 'Sucht nach Problemen, die im Hintergrund entstanden sind: fehlgeschlagene Schritte, Fehler oder Warnungen im Forma-Abgleich, verlorene Forma-Projekte, Stunden auf verlorenen Aufgaben, Outlook-Fehler, fehlende Sicherung, offene Monate, wartende Freigaben, sehr lange Tage und ein ruhender Forma-Abgleich. Jeder Befund verweist auf die passende Seite.',
        },
        {
          title: 'Jetzt prüfen und Excel',
          text: '**Jetzt prüfen** führt die Prüfung sofort aus. Jeder Bericht und die Liste lassen sich als Excel-Datei speichern.',
        },
        {
          title: 'Benachrichtigung',
          text: 'Hatte ein Bericht der letzten 36 Stunden Fehler oder Warnungen, erhält die technische Administration eine Benachrichtigung mit Verweis auf diesen Bericht.',
        },
      ],
      note: 'Die Berichte enthalten nur Anzahlen und Projektnummern, keine Namen, Stunden einzelner Personen oder Texte von Einträgen. Sie sieht, wer Einstellungen oder den Forma-Abgleich verwaltet.',
    },
  },
  en: {
    routine: {
      title: 'Recurring entries and convenience when booking',
      lead: 'Appointments that are the same every week, booking tasks by dragging, and lists the way you need them.',
      items: [
        {
          title: 'Repeat every week',
          text: 'In the booking dialog choose **Repeat every week**: weekdays, hours per day, from when and until when (end optional), e.g. for the weekly meeting. The entries are created as **drafts** when you open the week, the current week also overnight. Public holidays, leave and other days without target hours stay free. You check and submit them like any booking.',
        },
        {
          title: 'Pause, change, delete',
          text: 'In **My week** under **Recurring**. Paused or deleted, no new drafts are created; entries already created stay in your hours and can be changed or deleted there.',
        },
        {
          title: 'Drag tasks onto a day',
          text: 'Dragging a task from **Favourites** or **Recently used** onto a day opens the booking for that day. You order favourites by dragging; a recently used task dragged between the favourites becomes a favourite at that place. The day header turns green with a tick when the target hours are reached, and the warning colour when more is booked.',
        },
        {
          title: 'Dialogs and paragraphs',
          text: 'Dialogs can be moved by their title bar; the browser remembers the place on this device. On wide screens the booking dialog opens beside the week. The Enter key starts a new paragraph in the description; paragraphs are kept in lists, approvals and billing.',
        },
        {
          title: 'Sorting and searching',
          text: 'A click on a column heading sorts the list, a second one reverses the order, a third one resets it; the choice applies to your account on every device. **Ctrl+K** searches pages and tabs, with words from all three languages, as well as projects (number or name: straight to analysis, team, tasks …) and, with the matching right, people; always only what you may open.',
        },
      ],
      note: 'A recurring entry only books tasks you may book. When the task is closed, the entry shows “task no longer bookable” and no more drafts are created.',
    },
    leavedays: {
      title: 'Single leave days, sickness during leave and company events',
      lead: 'What applies when requesting, which days count and who sees what.',
      items: [
        {
          title: 'Single days',
          text: 'In the request choose **Single days** and click the working days in the calendar, also across several months. Weekends, public holidays and days off of the contract cannot be chosen and never count. Days with only such days between them are combined into one entry; the request applies completely or not at all.',
        },
        {
          title: 'Sick during leave',
          text: 'If you fall ill during leave, report it with a medical certificate to **HR**. HR enters the sickness and credits the leave days back; the leave is not extended. Your own sick report on leave days is therefore not possible.',
        },
        {
          title: 'Which days count',
          text: 'The preview in the request shows the leave days counted: the working days of your contract. For employees with the Turkish holiday calendar and a 5-day week the company can decide that a Saturday between two leave days counts. Sundays and public holidays never count.',
        },
        {
          title: 'Company events',
          text: 'Company events are shown in the **Team** calendar and on the start page, all day, half a day or with times. If an event is only for certain people, they see it in their row and, with the Outlook connection, as an appointment in their Outlook calendar. Company events are working time and do not reduce the target hours.',
        },
      ],
      note: 'Colleagues see “absent” in the team calendar; which kinds they can also recognise (usually vacation and training) is set by the company. Sickness and other health reasons are seen only by you and HR.',
    },
    projectrules: {
      title: 'Project rules: approval, templates, tasks and field values',
      lead: 'What is set per project. The settings are in the project under Overview, Tasks and Forma.',
      items: [
        {
          title: 'Approval of hours',
          text: 'Under **Overview > Approval of hours** each project chooses: **office** (one stage), **project lead** (one stage; without another project lead the office decides), **project lead, then office** or **own approval** with the submission. Without a choice of its own the company default applies. A change applies to everything not yet decided; approved hours stay approved.',
        },
        {
          title: 'Task templates',
          text: 'The office keeps lists of manual tasks under **Administration > Task templates**, e.g. for building services planning. In the project, whoever may create manual tasks there takes one over in a single step; titles the project already has are left out.',
        },
        {
          title: 'Manual tasks: number and deletion',
          text: 'Number and name can be changed, the number only to a free one; bookings stay assigned. Before **deleting**, the dialog shows what goes. Hours in a closed month or on an invoice prevent deletion (then only hide); drafts not yet submitted stay with the person. Deleting cannot be undone.',
        },
        {
          title: 'Field values per building',
          text: 'In the **Forma** tab under **Fields from Forma** use **Limit values** to set which values of a field the project uses, in the whole project, per building or per location, e.g. the floors of a building. The project analysis uses only these values; a task with another value counts as “not specified” there and is listed. Forma stays unchanged.',
        },
        {
          title: 'Project colour',
          text: 'Every project has a colour for the week, the calendar and the reports. New projects get the least used colour; change it when **editing** the project.',
        },
      ],
      note: 'With “own approval” the hours are approved with the submission; the person can withdraw that until the month is closed, and the office can correct.',
    },
    staffing: {
      title: 'Who works where, own projects and hour types',
      lead: 'An overview of who is on which project and what the figures of the reports mean.',
      items: [
        {
          title: 'My projects or all',
          text: 'The project list shows either **My projects** (member or project lead) or **All projects** you may see. The choice applies to your account; office and management start with all, everyone else with their own.',
        },
        {
          title: 'Who works where',
          text: 'On the **Projects** page below the list, **Who works where**: projects and people with the hours booked in the period, the darker the more, plus project leads and members without hours. Project leads see their projects; all projects are seen by those who may see the hours of others. Drafts of others do not count, the internal project is not included.',
        },
        {
          title: 'Hour types',
          text: 'Booked hours are split into **billable**, **included** (fixed fee), **project, not billable** and **internal**. Personal figures use booked hours only; billed hours are shown in the project analysis and in billing.',
        },
        {
          title: 'Coverage of the plan/actual ratio',
          text: 'The ratio rates only finished tasks with planned hours. If part of them has no plan, the report shows “without plan” with the hours; if the ratio covers less than half, a note is shown next to it. Then add planned hours in Forma or on the manual tasks.',
        },
      ],
      note: 'In the building view of the project analysis green shows the progress (finished tasks) and blue the hours; the legend is shown next to each.',
    },
    hrtools: {
      title: 'HR: reset the balance, sickness during leave, holidays and events',
      lead: 'Tools of HR; every change is logged and can be taken back where stated.',
      items: [
        {
          title: 'Reset the balance',
          text: 'Under **Absences > Extra hours**, **Reset balance** sets the balance of a person to 0, e.g. after the introduction phase: extra hours, target/actual and reminders about missing bookings count from the chosen day (not in the future). Bookings stay unchanged, payouts before it no longer count. The person is notified; undo is possible.',
        },
        {
          title: 'Sickness during leave',
          text: 'Only HR enters a sickness with a certificate on leave days: the dialog shows the days concerned, with **Shorten leave** the leave days go back (Germany: § 9 BUrlG; Turkey: İş Kanunu art. 56, Yıllık Ücretli İzin Yönetmeliği art. 6). The leave is not extended.',
        },
        {
          title: 'Single days for others',
          text: '**Single days** can also be entered for other people; consecutive days become one entry, weekends and public holidays never count.',
        },
        {
          title: 'Public holidays',
          text: 'Under **Absences > Public holidays** per calendar: rename, move, set to half a day, remove and restore standard holidays; add own days, every year if wanted (fixed dates only). Every step can be undone.',
        },
        {
          title: 'Company events',
          text: 'Under **Absences > Company events**: all day, half a day or with times, with a note and, if wanted, only for certain people. They see the event in their row, on the start page and, with the Outlook connection, as a “busy” appointment in Outlook (no invitation). Deleting can be undone.',
        },
      ],
      note: 'For people with the Turkish holiday calendar and a 5-day week, **Administration > Settings** decides whether a Saturday between two leave days counts as a leave day (default: off, more favourable for employees). The employment contract decides; please have it checked legally if in doubt.',
    },
    protection: {
      title: 'Roles, rights and additional confirmation',
      lead: 'Who may do what, how billing and customers are protected additionally and who reads which guide.',
      items: [
        {
          title: 'Roles and single rights',
          text: 'For every person under **Role and access** an overview shows which rights each role brings. Every right belongs to at least one role; employees and externals deliberately get no administration rights. Single rights can be granted in addition; each one says what it opens.',
        },
        {
          title: 'Additional confirmation',
          text: 'Whoever holds the right for billing or customers can switch on there that the module opens only after a new confirmation: with the code of the authenticator app, otherwise with the password. The confirmation lasts the minutes set (5 to 240). The server checks it on every call of the module.',
        },
        {
          title: 'Who sees which guide',
          text: 'Externals and employees read their own guide, project leads also the ones for employees and externals; office, management and technical administration see all tabs.',
        },
      ],
      note: 'Limits: the confirmation protects working in Projektzeit, not access to the server, the database or the backups. Whoever signs in only with Microsoft sets up the authenticator app in the profile for the confirmation. The texts of the guides are part of the application and not secret.',
    },
    reports: {
      title: 'Maintenance reports and the daily check',
      lead: 'What ran in the background at night and whether problems arose: under Administration > Maintenance reports.',
      items: [
        {
          title: 'One report per night',
          text: 'Every step of the nightly maintenance with result and duration: Forma import and sync, recurring entries, backup and retention, capacities, documents, export for reports and cleaning up old sign-ins.',
        },
        {
          title: 'Daily check',
          text: 'Looks for problems that arose in the background: failed steps, errors or warnings of the Forma sync, lost Forma projects, hours on lost tasks, Outlook errors, a missing backup, open months, waiting approvals, very long days and an idle Forma sync. Each finding links to the right page.',
        },
        {
          title: 'Check now and Excel',
          text: '**Check now** runs the check at once. Each report and the list can be saved as an Excel file.',
        },
        {
          title: 'Notification',
          text: 'If a report of the last 36 hours had errors or warnings, the technical administration gets a notification that links to that report.',
        },
      ],
      note: 'The reports contain counts and project numbers only, no names, hours of single people or texts of entries. They are seen by those who manage settings or the Forma sync.',
    },
  },
  tr: {
    routine: {
      title: 'Tekrarlanan kayıtlar ve kolay kayıt',
      lead: 'Her hafta aynı olan toplantılar, görevleri sürükleyerek kaydetme ve ihtiyacınıza göre sıralanan listeler.',
      items: [
        {
          title: 'Her hafta tekrarla',
          text: 'Kayıt penceresinde **Her hafta tekrarla** seçin: haftanın günleri, gün başına saat, başlangıç ve isteğe bağlı bitiş, örneğin haftalık toplantı için. Kayıtlar haftayı açtığınızda, içinde bulunulan hafta için gece de **taslak** olarak oluşur. Resmî tatiller, izin ve hedef saati olmayan diğer günler boş kalır. Taslakları her kayıt gibi kontrol edip gönderirsiniz.',
        },
        {
          title: 'Duraklatma, değiştirme, silme',
          text: '**Haftam** sayfasında **Tekrarlanan** altında. Duraklatılan veya silinen kurallar yeni taslak oluşturmaz; oluşmuş kayıtlar saatlerinizde kalır ve orada değiştirilebilir veya silinebilir.',
        },
        {
          title: 'Görevleri bir güne sürükleme',
          text: '**Favoriler** veya **Son kullanılanlar** listesinden bir görevi bir güne sürüklemek o gün için kaydı açar. Favorileri sürükleyerek sıralarsınız; favorilerin arasına sürüklenen son kullanılan bir görev o yerde favori olur. Hedef saate ulaşıldığında gün başlığı onay işaretiyle yeşil, fazla kayıt olduğunda uyarı rengiyle görünür.',
        },
        {
          title: 'Pencereler ve paragraflar',
          text: 'Pencereler başlık çubuğundan taşınabilir; tarayıcı yerini bu cihazda hatırlar. Geniş ekranlarda kayıt penceresi haftanın yanında açılır. Açıklamada Enter tuşu yeni bir paragraf başlatır; paragraflar listelerde, onaylarda ve faturalandırmada korunur.',
        },
        {
          title: 'Sıralama ve arama',
          text: 'Bir sütun başlığına tıklamak listeyi sıralar, ikinci tıklama sırayı tersine çevirir, üçüncüsü geri alır; seçim hesabınız için her cihazda geçerlidir. **Ctrl+K** sayfaları ve sekmeleri üç dildeki kelimelerle arar; ayrıca projeleri (numara veya ad: doğrudan analiz, ekip, görevler …) ve uygun yetkiyle kişileri bulur; her zaman yalnızca açabileceklerinizi.',
        },
      ],
      note: 'Tekrarlanan bir kayıt yalnızca sizin kayıt yapabildiğiniz görevlere kayıt yapar. Görev kapatılırsa kayıtta „görev artık kayda açık değil“ yazar ve yeni taslak oluşmaz.',
    },
    leavedays: {
      title: 'Tek tek izin günleri, izinde hastalık ve firma etkinlikleri',
      lead: 'Talep ederken neyin geçerli olduğu, hangi günlerin sayıldığı ve kimin neyi gördüğü.',
      items: [
        {
          title: 'Tek tek günler',
          text: 'Talepte **Tek tek günler** seçip takvimde iş günlerine tıklayın, birden fazla ay boyunca da olabilir. Hafta sonları, resmî tatiller ve sözleşmedeki boş günler seçilemez ve hiçbir zaman sayılmaz. Aralarında yalnızca bu tür günler bulunan günler tek bir kayıtta birleşir; talep ya tamamen ya da hiç geçerli olur.',
        },
        {
          title: 'İzinde hastalanma',
          text: 'İzindeyken hastalanırsanız durumu sağlık raporuyla **personel birimine** bildirin. Personel birimi hastalığı girer ve izin günlerini iade eder; izin bu nedenle uzamaz. Bu yüzden izin günlerine kendi hastalık bildiriminizi giremezsiniz.',
        },
        {
          title: 'Hangi günler sayılır',
          text: 'Talepteki ön izleme sayılan izin günlerini gösterir: sözleşmenizdeki iş günleri. Türk resmî tatil takvimine ve 5 günlük çalışma haftasına sahip çalışanlar için firma, iki izin günü arasındaki cumartesinin de sayılmasını belirleyebilir. Pazar günleri ve resmî tatiller hiçbir zaman sayılmaz.',
        },
        {
          title: 'Firma etkinlikleri',
          text: 'Firma etkinlikleri **Ekip** takviminde ve ana sayfada tam gün, yarım gün veya saatleriyle görünür. Bir etkinlik yalnızca belirli kişiler içinse, bu kişiler onu kendi satırlarında ve Outlook bağlantısı açıksa Outlook takvimlerinde randevu olarak görür. Firma etkinlikleri çalışma süresidir ve hedef saati azaltmaz.',
        },
      ],
      note: 'Ekip arkadaşları ekip takviminde „yok“ görür; ayrıca hangi türleri tanıyabilecekleri (genellikle yıllık izin ve eğitim) firma tarafından belirlenir. Hastalığı ve diğer sağlık nedenlerini yalnızca siz ve personel birimi görür.',
    },
    projectrules: {
      title: 'Proje kuralları: onay, şablonlar, görevler ve alan değerleri',
      lead: 'Proje başına belirlenenler. Ayarlar projede Genel, Görevler ve Forma altında bulunur.',
      items: [
        {
          title: 'Saatlerin onayı',
          text: '**Genel > Saatlerin onayı** altında her proje seçer: **ofis** (tek aşama), **proje lideri** (tek aşama; başka proje lideri yoksa ofis karar verir), **önce proje lideri, sonra ofis** veya göndermeyle **kişinin kendi onayı**. Projenin kendi seçimi yoksa firma varsayılanı geçerlidir. Değişiklik henüz karar verilmemiş tüm kayıtlar için geçerlidir; onaylanmış saatler onaylı kalır.',
        },
        {
          title: 'Görev şablonları',
          text: 'Ofis, **Sistem yönetimi > Görev şablonları** altında manuel görev listeleri tutar, örneğin TGA planlaması için. Projede manuel görev oluşturabilen kişi şablonu tek adımda aktarır; projede zaten bulunan başlıklar atlanır.',
        },
        {
          title: 'Manuel görevler: numara ve silme',
          text: 'Numara ve ad değiştirilebilir, numara yalnızca boş bir numaraya; kayıtlar görevde kalır. **Silmeden** önce pencere neyin kaldırılacağını gösterir. Kapanmış bir aydaki veya bir faturadaki saatler silmeyi engeller (o zaman yalnızca gizlenir); henüz gönderilmemiş taslaklar kişide kalır. Silme geri alınamaz.',
        },
        {
          title: 'Bina başına alan değerleri',
          text: '**Forma** sekmesinde **Forma alanları** altında **Değerleri sınırla** ile projenin bir alanın hangi değerlerini kullandığını belirleyin: tüm projede, bina başına veya konum başına, örneğin bir binanın katları. Proje analizi yalnızca bu değerleri kullanır; başka bir değere sahip görev orada „belirtilmemiş“ sayılır ve listelenir. Forma değişmez.',
        },
        {
          title: 'Proje rengi',
          text: 'Her projenin hafta, takvim ve raporlar için bir rengi vardır. Yeni projeler en az kullanılan rengi alır; projeyi **düzenlerken** değiştirilebilir.',
        },
      ],
      note: '„Kişinin kendi onayı“ seçildiğinde saatler gönderimle onaylanır; kişi bunu ay kapanışına kadar geri çekebilir, ofis düzeltebilir.',
    },
    staffing: {
      title: 'Kim nerede çalışıyor, kendi projeler ve saat türleri',
      lead: 'Projelerde kimin çalıştığına ve raporlardaki rakamların ne anlama geldiğine genel bakış.',
      items: [
        {
          title: 'Projelerim veya tümü',
          text: 'Proje listesi isteğe göre **Projelerim** (üye veya proje lideri) ya da görebildiğiniz **Tüm projeler** listesini gösterir. Seçim hesabınız için geçerlidir; ofis ve yönetim tümüyle, diğer herkes kendi projeleriyle başlar.',
        },
        {
          title: 'Kim nerede çalışıyor',
          text: '**Projeler** sayfasında listenin altında, **Kim nerede çalışıyor**: dönem içinde kaydedilen saatlerle projeler ve kişiler (ne kadar koyu, o kadar çok), ayrıca saati olmayan proje liderleri ve üyeler. Proje lideri kendi projelerini görür; tüm projeleri başkalarının saatlerini görme yetkisi olan kişiler görür. Başkalarının taslakları sayılmaz, iç proje dahil değildir.',
        },
        {
          title: 'Saat türleri',
          text: 'Kaydedilen saatler **faturalandırılabilir**, **dahil** (götürü ücret), **proje, faturalandırılmaz** ve **iç** olarak ayrılır. Kişisel göstergeler yalnızca kaydedilen saatleri kullanır; faturalandırılan saatler proje analizinde ve faturalandırmada yer alır.',
        },
        {
          title: 'Plan-gerçekleşen oranının kapsamı',
          text: 'Oran yalnızca planlanan saati olan tamamlanmış görevleri değerlendirir. Bir kısmında plan yoksa rapor „plansız“ ibaresini saatleriyle gösterir; oran yarıdan azını kapsıyorsa yanında bir not çıkar. Bu durumda planlanan saatleri Forma’da veya manuel görevlerde tamamlayın.',
        },
      ],
      note: 'Proje analizinin bina görünümünde yeşil ilerlemeyi (tamamlanan görevler), mavi saatleri gösterir; açıklama her birinin yanında yer alır.',
    },
    hrtools: {
      title: 'Personel: bakiyeyi sıfırlama, izinde hastalık, tatiller ve etkinlikler',
      lead: 'Personel biriminin araçları; her değişiklik kayda geçer ve belirtilen yerlerde geri alınabilir.',
      items: [
        {
          title: 'Bakiyeyi sıfırla',
          text: '**Devamsızlıklar > Fazla mesai** altında **Bakiyeyi sıfırla** bir kişinin bakiyesini 0’a çeker, örneğin tanışma döneminden sonra: fazla mesai, hedef/gerçekleşen ve eksik kayıt hatırlatmaları seçilen günden itibaren sayılır (gelecekte bir gün olamaz). Kayıtlar değişmez, önceki ödemeler artık sayılmaz. Kişi bilgilendirilir; geri alınabilir.',
        },
        {
          title: 'İzinde hastalık',
          text: 'İzin günlerine sağlık raporlu hastalığı yalnızca personel birimi girer: pencere ilgili günleri gösterir, **İzni kısalt** ile izin günleri iade edilir (Almanya: § 9 BUrlG; Türkiye: İş Kanunu m. 56, Yıllık Ücretli İzin Yönetmeliği m. 6). İzin uzamaz.',
        },
        {
          title: 'Başkaları için tek tek günler',
          text: '**Tek tek günler** başka kişiler için de girilebilir; ardışık günler tek kayıt olur, hafta sonları ve resmî tatiller hiçbir zaman sayılmaz.',
        },
        {
          title: 'Resmî tatiller',
          text: '**Devamsızlıklar > Resmi tatiller** altında takvim başına: standart tatilleri yeniden adlandırın, taşıyın, yarım güne çevirin, kaldırın ve geri getirin; kendi günlerinizi ekleyin, istenirse her yıl (yalnızca sabit tarihler). Her adım geri alınabilir.',
        },
        {
          title: 'Firma etkinlikleri',
          text: '**Devamsızlıklar > Firma etkinlikleri** altında: tam gün, yarım gün veya saatleriyle, notla ve istenirse yalnızca belirli kişiler için. Bu kişiler etkinliği kendi satırlarında, ana sayfada ve Outlook bağlantısı açıksa Outlook’ta „meşgul“ randevu olarak görür (davet gönderilmez). Silme geri alınabilir.',
        },
      ],
      note: 'Türk resmî tatil takvimine ve 5 günlük çalışma haftasına sahip kişiler için **Sistem yönetimi > Ayarlar**, iki izin günü arasındaki cumartesinin izin günü sayılıp sayılmayacağını belirler (varsayılan: kapalı, çalışanın lehine). Belirleyici olan iş sözleşmesidir; tereddüt hâlinde hukuki olarak kontrol ettirin.',
    },
    protection: {
      title: 'Roller, yetkiler ve ek doğrulama',
      lead: 'Kimin neyi yapabildiği, faturalandırma ve müşterilerin nasıl ek olarak korunduğu ve kimin hangi kılavuzu okuduğu.',
      items: [
        {
          title: 'Roller ve tekil yetkiler',
          text: 'Her kişide **Rol ve erişim** altındaki genel bakış her rolün hangi yetkilerle geldiğini gösterir. Her yetki en az bir role aittir; çalışanlar ve dış kişiler bilinçli olarak yönetim yetkisi almaz. Tekil yetkiler ayrıca verilebilir; her birinde neyi açtığı yazar.',
        },
        {
          title: 'Ek doğrulama',
          text: 'Faturalandırma veya müşteriler yetkisine sahip olan kişi, modülün yalnızca yeniden doğrulamadan sonra açılmasını orada açabilir: doğrulayıcı uygulamanın koduyla, yoksa parolayla. Doğrulama ayarlanan dakika boyunca geçerlidir (5 ile 240 arası). Sunucu bunu modülün her çağrısında kontrol eder.',
        },
        {
          title: 'Kim hangi kılavuzu görür',
          text: 'Dış kişiler ve çalışanlar kendi kılavuzlarını, proje liderleri ayrıca çalışanlar ve dış kişiler için olanları okur; ofis, yönetim ve teknik sistem yönetimi tüm sekmeleri görür.',
        },
      ],
      note: 'Sınırlar: doğrulama Projektzeit içindeki kullanımı korur; sunucuya, veritabanına veya yedeklere erişimi korumaz. Yalnızca Microsoft ile oturum açan kişi doğrulama için profilde doğrulayıcı uygulamayı kurar. Kılavuz metinleri uygulamanın parçasıdır ve gizli değildir.',
    },
    reports: {
      title: 'Bakım raporları ve günlük kontrol',
      lead: 'Gece arka planda neyin çalıştığı ve sorun çıkıp çıkmadığı: Sistem yönetimi > Bakım raporları altında.',
      items: [
        {
          title: 'Her gece bir rapor',
          text: 'Gece bakımının her adımı sonucu ve süresiyle: Forma içe aktarımı ve eşitleme, tekrarlanan kayıtlar, yedekleme ve saklama, kapasiteler, belgeler, raporlar için dışa aktarım ve eski oturumların temizlenmesi.',
        },
        {
          title: 'Günlük kontrol',
          text: 'Arka planda ortaya çıkan sorunları arar: başarısız adımlar, Forma eşitlemesindeki hatalar veya uyarılar, kaybolan Forma projeleri, kaybolan görevlerdeki saatler, Outlook hataları, eksik yedek, açık aylar, bekleyen onaylar, çok uzun günler ve duran bir Forma eşitlemesi. Her bulgu ilgili sayfaya bağlantı verir.',
        },
        {
          title: 'Şimdi kontrol et ve Excel',
          text: '**Şimdi kontrol et** kontrolü hemen çalıştırır. Her rapor ve liste Excel dosyası olarak kaydedilebilir.',
        },
        {
          title: 'Bildirim',
          text: 'Son 36 saatteki bir rapor hata veya uyarı içerdiyse, teknik sistem yönetimi bu rapora bağlantı veren bir bildirim alır.',
        },
      ],
      note: 'Raporlar yalnızca sayılar ve proje numaraları içerir; isim, tek tek kişilerin saatleri veya kayıt metinleri içermez. Raporları ayarları veya Forma eşitlemesini yöneten kişiler görür.',
    },
  },
};
